import { useId, useState } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import {
  calculateAnnualTimeSavingsBreakdown,
  calculateTimeSavingsDisplay,
  buildCumulativeWorkloadComparison,
  normalizeTimeSavingsInput,
  TIME_SAVINGS_DEFAULT_INPUTS,
} from '../utils/timeSavings';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';
import { ArrowRight } from './Icons';
import { BrandButton } from './ui/BrandButton';

const WEEKLY_SEARCHES_MAX = 50;
const MONTHLY_TRANSPORTS_MAX = 100;
const CHART_WIDTH = 720;
const CHART_HEIGHT = 300;
const CHART_PADDING = 32;
const CHART_HEADROOM_HOURS = 100;
const SLIDER_CLASS_NAME = [
  'brand-focus-ring h-11 w-full cursor-pointer touch-pan-y appearance-none rounded-full border-0 bg-transparent bg-[length:100%_0.125rem] bg-center bg-no-repeat',
  '[&::-webkit-slider-runnable-track]:h-0.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent',
  '[&::-webkit-slider-thumb]:-mt-[0.4375rem] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[rgb(var(--theme-surface))] [&::-webkit-slider-thumb]:bg-[rgb(var(--theme-primary))] [&::-webkit-slider-thumb]:shadow-[0_0_0_1px_rgb(var(--theme-primary))]',
  '[&::-moz-range-track]:h-0.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent',
  '[&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-[rgb(var(--theme-surface))] [&::-moz-range-thumb]:bg-[rgb(var(--theme-primary))] [&::-moz-range-thumb]:shadow-[0_0_0_1px_rgb(var(--theme-primary))]',
].join(' ');

function formatTimeValue(value: number, language: string): string {
  return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
    maximumFractionDigits: value < 1 ? 2 : 1,
  }).format(value);
}

function curvePoints(values: number[], maximum: number): Array<{ x: number; y: number }> {
  return values.map((value, index) => ({
    x: CHART_PADDING + index * ((CHART_WIDTH - CHART_PADDING * 2) / (values.length - 1)),
    y: CHART_HEIGHT - CHART_PADDING - value / maximum * (CHART_HEIGHT - CHART_PADDING * 2),
  }));
}

function linePath(points: Array<{ x: number; y: number }>): string {
  const firstPoint = points[0];
  if (!firstPoint) return '';

  return points.slice(1).reduce(
    (path, point) => `${path} L ${point.x.toFixed(1)} ${point.y.toFixed(1)}`,
    `M ${firstPoint.x.toFixed(1)} ${firstPoint.y.toFixed(1)}`,
  );
}

function sliderBackground(value: number, maximum: number) {
  const progress = value / maximum * 100;

  return {
    backgroundImage: `linear-gradient(to right, rgb(var(--theme-primary)) 0%, rgb(var(--theme-primary)) ${progress}%, rgb(var(--theme-border)) ${progress}%, rgb(var(--theme-border)) 100%)`,
  };
}

function TimeSavingsCallToAction({
  label,
  onGetStarted,
  onStartIntent,
}: {
  label: string;
  onGetStarted: () => void;
  onStartIntent?: () => void;
}) {
  return (
    <BrandButton
      type="button"
      onClick={onGetStarted}
      onFocus={onStartIntent}
      onPointerEnter={onStartIntent}
      onPointerDown={onStartIntent}
      size="md"
      className="group"
    >
      {label}
      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1 motion-reduce:transition-none" aria-hidden="true" />
    </BrandButton>
  );
}

export function LandingTimeSavingsSimulator({
  onGetStarted,
  onStartIntent,
}: {
  onGetStarted: () => void;
  onStartIntent?: () => void;
}) {
  const { language, t } = useTranslation();
  const weeklyScaleId = useId().replace(/:/g, '');
  const monthlyScaleId = useId().replace(/:/g, '');
  const savingsGradientId = useId().replace(/:/g, '');
  const [weeklySearches, setWeeklySearches] = useState<number>(TIME_SAVINGS_DEFAULT_INPUTS.weeklySearches);
  const [monthlyTransports, setMonthlyTransports] = useState<number>(TIME_SAVINGS_DEFAULT_INPUTS.monthlyTransports);
  const { searchHours, transportHours } = calculateAnnualTimeSavingsBreakdown({ monthlyTransports, weeklySearches });
  const annualHours = searchHours + transportHours;
  const displayedSavings = calculateTimeSavingsDisplay(annualHours);
  const { withPalenHours, withoutPalenHours } = buildCumulativeWorkloadComparison({ monthlyTransports, weeklySearches });
  const chartMaximum = Math.max((withoutPalenHours[withoutPalenHours.length - 1] ?? 0) + CHART_HEADROOM_HOURS, 1);
  const withoutPalenPoints = curvePoints(withoutPalenHours, chartMaximum);
  const withPalenPoints = curvePoints(withPalenHours, chartMaximum);
  const withoutPalenPath = linePath(withoutPalenPoints);
  const withPalenPath = linePath(withPalenPoints);
  const savingsAreaPath = `${withoutPalenPath} ${[...withPalenPoints].reverse().map(({ x, y }) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')} Z`;
  const labelPointIndex = 8;
  const savingsLabelMiddle = ((withoutPalenPoints[labelPointIndex]?.y ?? 0) + (withPalenPoints[labelPointIndex]?.y ?? 0)) / 2;
  const savingsLabelY = Math.min(Math.max(savingsLabelMiddle - 30, 42), CHART_HEIGHT - 88);
  const savingsLabelTop = (savingsLabelY + 31) / CHART_HEIGHT * 100;
  const withoutPalenEndPoint = withoutPalenPoints[withoutPalenPoints.length - 1];
  const withPalenEndPoint = withPalenPoints[withPalenPoints.length - 1];

  return (
    <section id="time-savings" className={landingSectionClassName({ tone: 'white', className: 'px-4 py-14 sm:px-6 sm:py-28' })}>
      <div className="mx-auto grid w-full max-w-7xl gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-16">
        <div className="contents lg:order-1 lg:block">
          <h2 data-landing-scroll-target className={`${LANDING_SECTION_TITLE_CLASS_NAME} !font-normal text-center lg:text-left`}>
            <span className="lg:hidden">
              {t('timeSavings.titlePrefix')}<strong className="font-black">{t('timeSavings.titleEmphasis')}</strong>
            </span>
            <span className="hidden lg:inline">
              {t('timeSavings.titlePrefix')}<strong className="font-black">{t('timeSavings.titleEmphasis')}</strong>
            </span>
          </h2>

          <div data-testid="simulator-controls" className="order-3 grid gap-2 sm:gap-4 lg:mt-8">
            <label className="theme-heading-solid grid gap-1 rounded-xl border border-[rgb(var(--theme-border)/0.72)] bg-[rgb(var(--theme-surface)/0.92)] px-4 py-3 text-xs font-semibold shadow-[0_8px_24px_rgb(var(--theme-shadow)/0.08)] sm:gap-2 sm:px-5 sm:py-4 sm:text-sm">
              <span className="flex items-start justify-between gap-4">
                <span className="max-w-md">{t('timeSavings.weeklySearches')} <strong className="brand-text-strong">{t('timeSavings.perWeek')}</strong></span>
                <output className="brand-text-strong shrink-0 text-2xl font-bold tabular-nums">{weeklySearches}</output>
              </span>
              <input
                className={SLIDER_CLASS_NAME}
                min="0"
                max={WEEKLY_SEARCHES_MAX}
                list={weeklyScaleId}
                step="1"
                type="range"
                value={weeklySearches}
                style={sliderBackground(weeklySearches, WEEKLY_SEARCHES_MAX)}
                onChange={(event) => setWeeklySearches(normalizeTimeSavingsInput(event.currentTarget.valueAsNumber))}
              />
              <datalist id={weeklyScaleId}><option value="0" /><option value="10" /><option value="20" /><option value="30" /><option value="40" /><option value="50" /></datalist>
              <span className="theme-subtitle flex justify-between text-[10px] font-medium tabular-nums" aria-hidden="true"><span>0</span><span>10</span><span>20</span><span>30</span><span>40</span><span>50</span></span>
            </label>
            <label className="theme-heading-solid grid gap-1 rounded-xl border border-[rgb(var(--theme-border)/0.72)] bg-[rgb(var(--theme-surface)/0.92)] px-4 py-3 text-xs font-semibold shadow-[0_8px_24px_rgb(var(--theme-shadow)/0.08)] sm:gap-2 sm:px-5 sm:py-4 sm:text-sm">
              <span className="flex items-start justify-between gap-4">
                <span className="max-w-md">{t('timeSavings.monthlyTransports')} <strong className="brand-text-strong">{t('timeSavings.perMonth')}</strong></span>
                <output className="brand-text-strong shrink-0 text-2xl font-bold tabular-nums">{monthlyTransports}</output>
              </span>
              <input
                className={SLIDER_CLASS_NAME}
                min="0"
                max={MONTHLY_TRANSPORTS_MAX}
                list={monthlyScaleId}
                step="1"
                type="range"
                value={monthlyTransports}
                style={sliderBackground(monthlyTransports, MONTHLY_TRANSPORTS_MAX)}
                onChange={(event) => setMonthlyTransports(normalizeTimeSavingsInput(event.currentTarget.valueAsNumber))}
              />
              <datalist id={monthlyScaleId}><option value="0" /><option value="20" /><option value="40" /><option value="60" /><option value="80" /><option value="100" /></datalist>
              <span className="theme-subtitle flex justify-between text-[10px] font-medium tabular-nums" aria-hidden="true"><span>0</span><span>20</span><span>40</span><span>60</span><span>80</span><span>100</span></span>
            </label>
          </div>

          <div data-testid="time-savings-mobile-cta" className="order-4 flex justify-center lg:hidden">
            <TimeSavingsCallToAction
              label={t('timeSavings.cta')}
              onGetStarted={onGetStarted}
              onStartIntent={onStartIntent}
            />
          </div>
        </div>

        <div data-testid="simulator-chart" className="order-2 min-w-0 lg:order-2">
          <h3 data-testid="annual-saved-time" className="theme-heading-solid mb-3 text-center text-2xl font-black leading-[1.08] tracking-[-0.04em] sm:mb-7 sm:text-4xl md:text-5xl lg:text-left" aria-live="polite">
            <span data-testid="annual-saved-time-value" className="text-green-400">{formatTimeValue(displayedSavings.value, language)} {t(`timeSavings.${displayedSavings.unit}Short`)}</span>{' '}
            {t(`timeSavings.${displayedSavings.unit}SavedOver`)} <strong className="brand-text-strong">{t('timeSavings.oneYearValue')}</strong>
          </h3>

          <div role="group" aria-label={t('timeSavings.chartLabel')}>
            <div data-testid="savings-plot" className="relative -mt-2 sm:mt-0">
              <svg className="h-auto w-full overflow-visible" viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" aria-label={t('timeSavings.chartLabel')}>
              <defs>
                <linearGradient id={savingsGradientId} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgb(22 163 74)" stopOpacity="0.14" />
                  <stop offset="100%" stopColor="rgb(22 163 74)" stopOpacity="0.03" />
                </linearGradient>
              </defs>
              <g stroke="rgb(var(--theme-border))" strokeDasharray="3 5" strokeOpacity="0.55">
                <line x1={CHART_PADDING} x2={CHART_WIDTH - CHART_PADDING} y1="91" y2="91" />
                <line x1={CHART_PADDING} x2={CHART_WIDTH - CHART_PADDING} y1="150" y2="150" />
                <line x1={CHART_PADDING} x2={CHART_WIDTH - CHART_PADDING} y1="209" y2="209" />
                <line x1={CHART_PADDING} x2={CHART_WIDTH - CHART_PADDING} y1={CHART_HEIGHT - CHART_PADDING} y2={CHART_HEIGHT - CHART_PADDING} />
              </g>
              <g data-testid="savings-area">
                <path d={savingsAreaPath} fill={`url(#${savingsGradientId})`} />
                <path data-testid="with-palen-curve" d={withPalenPath} className="transition-[d] duration-300 motion-reduce:transition-none" fill="none" stroke="rgb(22 163 74)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
                {withPalenEndPoint && <circle cx={withPalenEndPoint.x} cy={withPalenEndPoint.y} r="5" fill="rgb(22 163 74)" stroke="rgb(var(--theme-surface))" strokeWidth="2" />}
              </g>
              <path data-testid="without-palen-curve" d={withoutPalenPath} className="transition-[d] duration-300 motion-reduce:transition-none" fill="none" stroke="rgb(var(--theme-primary))" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
              {withoutPalenEndPoint && <circle cx={withoutPalenEndPoint.x} cy={withoutPalenEndPoint.y} r="5" fill="rgb(var(--theme-primary))" stroke="rgb(var(--theme-surface))" strokeWidth="2" />}
              <text x={CHART_PADDING} y={CHART_HEIGHT - 5} fill="rgb(var(--theme-muted))" className="text-xs">{t('timeSavings.now')}</text>
              <text x={CHART_WIDTH - CHART_PADDING} y={CHART_HEIGHT - 5} textAnchor="end" fill="rgb(var(--theme-primary-strong))" className="text-xs font-bold">{t('timeSavings.oneYearValue')}</text>
              </svg>
              <div
                data-testid="savings-area-label"
                className="theme-rating-saved pointer-events-none absolute right-8 grid w-max max-w-40 -translate-y-1/2 rounded-md border border-green-600/15 bg-[rgb(var(--theme-surface)/0.9)] px-2 py-1 text-right shadow-sm sm:max-w-52"
                style={{ top: `${savingsLabelTop}%` }}
              >
                <span className="text-xs font-semibold tabular-nums sm:text-sm">
                  <span data-testid="savings-area-label-value">{formatTimeValue(displayedSavings.value, language)} {t(`timeSavings.${displayedSavings.unit}Short`)}</span>{' '}
                  <span>{t(`timeSavings.${displayedSavings.unit}GainedSuffix`)}</span>
                </span>
              </div>
            </div>
            <div className="theme-subtitle mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[11px] font-semibold sm:text-xs">
              <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded-full bg-[rgb(var(--theme-primary))]" aria-hidden="true" />{t('timeSavings.withoutPalen')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded-full bg-green-600" aria-hidden="true" />{t('timeSavings.withPalen')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-sm border border-green-600/10 bg-green-600/10" aria-hidden="true" />{t('timeSavings.savedArea')}</span>
            </div>
          </div>
          <p className="theme-subtitle mt-4 text-xs leading-relaxed">{t('timeSavings.methodology')}</p>
          <div data-testid="time-savings-desktop-cta" className="mt-6 hidden justify-center lg:flex lg:justify-start">
            <TimeSavingsCallToAction
              label={t('timeSavings.cta')}
              onGetStarted={onGetStarted}
              onStartIntent={onStartIntent}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
